import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import authService from '../services/authService';
import { ApiService } from '../services/api-services/ApiService';

import { logger } from '../utils/logger';
import { useAuthStore } from '../store/authStore';
import { StorageUtils } from '../utils/StorageUtils';
import { parseValidationError } from '../services/errorService';
import { AuthContext } from './AuthContext.context';
import { globalActions } from '../store';

// Types
import { User, DoctorAvailabilityStatus, UserStatus, UserRole, RegisterRequest } from '../types/User';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ApiUserInput = Record<string, any>;

// Fonction utilitaire pour convertir User API vers User principal
const convertApiUserToUser = (apiUser: ApiUserInput): User => {
  const userObj = {
    ...apiUser,
    // S'assurer que l'ID est conservé (chaîne UUID ou nombre)
    id: apiUser.id ?? '',
    name: apiUser.name ?? '',
    email: apiUser.email ?? '',
    // Ajouter les champs manquants avec des valeurs par défaut
    emailHash: apiUser.emailHash ?? '',
    password: apiUser.password ?? '',
    role: (apiUser.role || '').toUpperCase() as UserRole,
    // Convertir les champs snake_case en camelCase si nécessaire
    phoneNumber: apiUser.phone_number || apiUser.phoneNumber,
    residence: apiUser.residence || apiUser.city,
    speciality: apiUser.speciality || apiUser.specialty,
    licenseNumber: apiUser.license_number || apiUser.licenseNumber,
    availabilityStatus: (apiUser.availabilityStatus as DoctorAvailabilityStatus) || DoctorAvailabilityStatus.OFFLINE,
    totalConsultations: apiUser.totalConsultations || 0,
    status: apiUser.status === 'active' ? UserStatus.ACTIVE :
            apiUser.status === 'suspended' ? UserStatus.SUSPENDED :
            UserStatus.INACTIVE,
    isActive: apiUser.isActive !== false,
    mfaEnabled: apiUser.mfaEnabled || false,
    countryCode: apiUser.country_code || apiUser.countryCode || 'BJ',
    dialCode: apiUser.dial_code || apiUser.dialCode || undefined,
    createdAt: typeof apiUser.createdAt === 'object' ? (apiUser.createdAt as Date).toISOString() : (apiUser.createdAt ?? new Date().toISOString()),
    updatedAt: typeof apiUser.updatedAt === 'object' ? (apiUser.updatedAt as Date).toISOString() : (apiUser.updatedAt ?? new Date().toISOString()),
    preferences: apiUser.preferences || {},
    avatar: apiUser.avatar,
    clinicMemberships: apiUser.clinicMemberships,
    activeClinicId: apiUser.activeClinicId,
    activeClinicRole: apiUser.activeClinicRole,
  } as User;

  // Auto-synchronisation de la clinique active si non définie dans localStorage
  if (apiUser.activeClinicId && !localStorage.getItem('activeClinicId')) {
    localStorage.setItem('activeClinicId', apiUser.activeClinicId);
  }
  if (apiUser.activeClinicRole && !localStorage.getItem('activeClinicRole')) {
    localStorage.setItem('activeClinicRole', apiUser.activeClinicRole);
  }

  return userObj;
};

export interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');

  // Utilise uniquement Zustand store, pas de state local dupliqué
  const {
    user,
    isAuthenticated,
    isLoading: loading,
    setUser,
    setTokens,
    logout: storeLogout,
    setLoading,
  } = useAuthStore();

  // État d'erreur local (car Zustand n'a pas d'erreur)
  const [error, setError] = React.useState<string | null>(null);

  // ✅ ROUTE-2: Synchroniser l'état d'auth avec l'URL/routes protégées
  // Si l'utilisateur essaie d'accéder directement via une URL tout en étant non authentifié
  useEffect(() => {
    const currentPath = window.location.pathname;
    const isProtectedPath = ['/patient', '/doctor', '/admin'].some((path) =>
      currentPath.startsWith(path)
    );

    // Si le chemin est protégé mais l'utilisateur n'est pas authentifié, rediriger
    if (isProtectedPath && !isAuthenticated && !loading) {
      logger.warn("🚫 ROUTE-2: Tentative d'accès direct à route protégée sans auth", {
        attemptedPath: currentPath,
        isAuthenticated,
      });
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  const checkAuthStatus = useCallback(async () => {
    logger.debug("🔍 [AuthContext] Vérification du statut d'authentification...");
    const store = useAuthStore.getState();
    store.setLoading(true);

    // Utiliser le service pour récupérer les données stockées
    const authData = authService.getStoredData();

    // ✅ RÉSILIE-1: Tenter une vérification même si pas de token en storage (cas des Cookies/SSO)
    try {
      logger.debug("🔍 [AuthContext] Tentative de récupération de session (Cookies/Token)...");
      const response = await authService.verifyToken();

      if (response?.user) {
        // Session confirmée (soit par token stocké, soit par cookie)
        const convertedUser = convertApiUserToUser(response.user);
        store.setUser(convertedUser);
        StorageUtils.setUser(response.user as unknown as import('../types/api').User);
        
        // Stocker le token CSRF s'il est renvoyé
        const responseData = response as Record<string, unknown>;
        if (responseData.csrfToken) {
          StorageUtils.setCsrfToken(responseData.csrfToken as string);
        }
        
        // Si on a récupéré la session via cookie mais qu'on n'a pas de token local,
        // on tente de récupérer le token depuis la réponse (si le backend l'a renvoyé)
        const tokenFromResponse = (response as Record<string, unknown> & { token?: string })?.token;
        if (tokenFromResponse && !authData.token) {
          StorageUtils.setToken(tokenFromResponse);
          store.setTokens(tokenFromResponse, ''); 
          logger.debug('✅ [AuthContext] Token récupéré depuis la réponse et stocké');
        }
        
        logger.debug('✅ [AuthContext] Session active confirmée');
        // ✅ CORRECTION: Initialiser le presenceStore maintenant que l'auth est confirmée
        globalActions.initialize();
      } else {
        // Token explicitement invalide / expiré (401) ou pas de session
        logger.debug('🔄 [AuthContext] Pas de session active, nettoyage...');
        await authService.logout().catch(() => {});
        store.logout();
      }
    } catch (error: unknown) {
      // Erreur réseau, 429, 5xx, etc. → ne pas déconnecter brutalement si on a des données locales
      logger.debug(
        '⚠️ [AuthContext] Échec vérification session (non-401), utilisation du cache local...',
        error
      );

      if (authData.user && authData.user.id && authData.token) {
        // Fallback sur l'utilisateur stocké localement (mode hors-ligne/dégradé)
        const convertedUser = convertApiUserToUser(authData.user);
        store.setUser(convertedUser);
      } else {
        store.logout();
      }
    }

    store.setLoading(false);
  }, []);

  // Vérifier l'authentification au chargement - une seule fois
  useEffect(() => {
    logger.debug("🚀 [AuthContext] Initialisation du contexte d'authentification");
    checkAuthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      // Passer un objet credentials attendu par authService pour éviter l'envoi de valeurs undefined
      const response = await authService.login({ email, password });
      const verifiedSession = await authService.verifyToken().catch(() => null);
      const safeUser = verifiedSession?.user ?? response.user;
      const convertedUser = convertApiUserToUser(safeUser);

      setUser(convertedUser);
      StorageUtils.setUser(safeUser as unknown as import('../types/api').User);

      // Stocker le token CSRF s'il est renvoyé
      const csrf = verifiedSession?.csrfToken || response?.csrfToken;
      if (csrf) {
        StorageUtils.setCsrfToken(csrf);
      }

      // Synchroniser avec Zustand store
      if (response.token && response.refreshToken) {
        setTokens(response.token, response.refreshToken);
        // Persister le refresh token dans localStorage
        StorageUtils.setRefreshToken(response.refreshToken);
      }

      logger.debug(
        '🔄 [AuthContext] Redirection après login vers le bon espace...',
        safeUser.role
      );

      // ✅ SÉCURITÉ: Mapping strict rôle → chemin d'accueil (évite /clinic_manager inexistant)
      const roleToPath: Record<string, string> = {
        admin: '/admin',
        saas_admin: '/admin',
        super_admin: '/admin',
        doctor: '/doctor',
        nurse: '/nurse',
        patient: '/patient',
        clinic_manager: '/clinic',
        clinic: '/clinic',
      };
      const targetPath = roleToPath[safeUser.role.toLowerCase()] || '/login';
      // ✅ CORRECTION: Initialiser le presenceStore après un login réussi
      globalActions.initialize();
      navigate(targetPath, { replace: true });
    } catch (error) {
      logger.error('Erreur lors de la connexion:', error);
      setError(t('errorMessages.invalidCredentials'));
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData: RegisterRequest): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      // Adapter les données pour le format attendu
      const registerData = {
        email: userData.email,
        password: userData.password,
        name: userData.name,
        role: userData.role,
        phone_number: userData.phoneNumber,
        residence: userData.residence,
        speciality: userData.speciality,
        license_number: userData.licenseNumber,
      };

      const data = await authService.register(registerData);
      const verifiedSession = await authService.verifyToken().catch(() => null);
      const safeUser = verifiedSession?.user ?? data.user;
      const convertedUser = convertApiUserToUser(safeUser);

      setUser(convertedUser);
      StorageUtils.setUser(safeUser as unknown as import('../types/api').User);

      // Stocker le token CSRF s'il est renvoyé
      const csrf = verifiedSession?.csrfToken || data?.csrfToken;
      if (csrf) {
        StorageUtils.setCsrfToken(csrf);
      }

      // Synchroniser avec Zustand store
      if (data.token && data.refreshToken) {
        setTokens(data.token, data.refreshToken);
        // Persister le refresh token dans localStorage
        StorageUtils.setRefreshToken(data.refreshToken);
      }

      logger.debug(
        '🔄 [AuthContext] Redirection après inscription vers:',
        `/${safeUser.role.toLowerCase()}`
      );

      // Petite pause pour s'assurer que l'état est synchronisé
      await new Promise(resolve => setTimeout(resolve, 100));

      // Marquer l'utilisateur comme venant de s'inscrire pour forcer l'onboarding
      sessionStorage.setItem('justRegistered', 'true');

      // Navigation directe
      let targetPath = `/${safeUser.role.toLowerCase()}`;
      if (safeUser.role.toLowerCase() === 'doctor') {
        targetPath = '/doctor/onboarding';
      }
      navigate(targetPath, { replace: true });
    } catch (error) {
      logger.error("Erreur lors de l'inscription:", error);
      const parsedError = parseValidationError(error);
      if (parsedError?.field === 'password') {
        setError(parsedError.message);
        throw new Error(parsedError.message);
      }
      setError(t('errorMessages.validationError'));
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (data: Partial<User>) => {
    try {
      // ✅ OPTIMISATION: Ne pas utiliser le loading global pour les updates de profil
      // car cela fait clignoter le ProtectedRoute (boucle infinie avec HealthQuest)
      setError(null);

      const response = await ApiService.getInstance().putExtracted<Record<string, unknown>>('/api/profiles', data);
      const updatedUser = { ...user, ...response, lastUpdated: Date.now() };
      const convertedUser = convertApiUserToUser(updatedUser);
      
      setUser(convertedUser);
      StorageUtils.setUser(convertedUser as unknown as import('../types/api').User);

      // Synchroniser avec Zustand store (patchUser = local uniquement, pas d'appel API)
      // updateUser déclencherait un PATCH /api/users/:id redondant qui échoue (400)
      useAuthStore.getState().patchUser(convertedUser);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage =
        error.response?.data?.error || error.message || t('authContext.profileUpdateError');
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const logout = async () => {
    // Éviter les appels multiples simultanés
    const isLoggingOut = useAuthStore.getState().isLoggingOut;
    if (isLoggingOut) {
      logger.debug('🚫 [AuthContext] Logout déjà en cours, ignoré');
      return;
    }

    logger.debug('🚺 [AuthContext] Début de la déconnexion...');
    useAuthStore.getState().setIsLoggingOut(true);

    // ✅ INVALIDER TOUT LE CACHE REACT QUERY (STATE-1)
    // Cela supprime les données mises en cache qui sont susceptibles de contenir
    // des informations sensibles de l'utilisateur précédent
    queryClient.clear();
    logger.debug('✅ [AuthContext] Cache React Query invalidé');

    // ✅ NETTOYER L'ÉTAT DU FORMULAIRE (STATE-2)
    // Depuis le localStorage et Zustand pour prévenir les fuites de données entre utilisateurs
    const formRelatedKeys = Object.keys(localStorage).filter(
      (key) =>
        key.includes('form') ||
        key.includes('input') ||
        key.includes('draft') ||
        key.includes('temp')
    );

    formRelatedKeys.forEach((key) => {
      localStorage.removeItem(key);
      logger.debug(`✅ Clé formulaire supprimée: ${key}`);
    });

    // Nettoyer immédiatement l'état Zustand
    storeLogout();

    try {
      // Utiliser le service pour la déconnexion
      await authService.logout();
      logger.debug('✅ [AuthContext] Déconnexion réussie');
    } catch (error) {
      logger.warn('⚠️ [AuthContext] Erreur déconnexion:', error);
    } finally {
      useAuthStore.getState().setIsLoggingOut(false);
    }

    logger.debug('🔄 [AuthContext] Redirection vers login...');
    navigate('/login', { replace: true });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        login,
        logout,
        register,
        updateProfile,
        isAuthenticated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
// eslint-disable-next-line react-refresh/only-export-components
export { useAuth } from '../hooks/useAuth';
// eslint-disable-next-line react-refresh/only-export-components
export { AuthContext } from './AuthContext.context';
export type { AuthContextType } from './AuthContext.context';

